import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import dayjs from "dayjs";
import { getCityColor, getStoreColor } from "../../constants/plasticColors";

interface IVolumesDataPoint {
  period: string;
  date: string;
  store_name?: string; // For location-based
  city?: string; // For city-based
  plastic_weight_per_week?: number;
  plastic_droppers_per_week?: number;
  new_plastic_droppers?: number;
  week_start?: string;
  week_end?: string;
  month_start?: string;
  year?: number;
  quarter?: string;
}

interface PlasticVolumesPerDroppersProps {
  data: IVolumesDataPoint[] | null;
  isLoading: boolean;
  filterType: "city" | "location";
  period: "weekly" | "monthly" | "quarterly" | "yearly";
}

export const PlasticVolumesPerDroppers = React.memo(
  ({ data, isLoading, filterType, period }: PlasticVolumesPerDroppersProps) => {
    // Transform data into chart format
    const { categories, series, seriesNames } = useMemo(() => {
      if (!data || data.length === 0) {
        return { categories: [], series: [], seriesNames: [] };
      }

      // Group data by location/city
      const groupedData: Record<string, Record<string, number>> = {};
      const allDates = new Set<string>();

      data.forEach((point) => {
        const key = filterType === "location" ? point.store_name : point.city;
        if (!key) return;

        let dateKey = "";
        if (period === "weekly" && point.week_start) {
          dateKey = dayjs(point.week_start).format("YYYY-MM-DD");
        } else if (period === "monthly") {
          dateKey = dayjs(point.date).format("YYYY-MM");
        } else if (period === "quarterly") {
          dateKey = `${point.year} ${point.quarter}`;
        } else if (period === "yearly") {
          dateKey = String(point.year);
        }

        allDates.add(dateKey);
        if (!groupedData[key]) {
          groupedData[key] = {};
        }
        groupedData[key][dateKey] = point.plastic_droppers_per_week || 0;
      });

      // Sort dates
      const sortedDates = Array.from(allDates).sort((a, b) => {
        if (period === "weekly") {
          return dayjs(a).valueOf() - dayjs(b).valueOf();
        } else if (period === "monthly") {
          return dayjs(a).valueOf() - dayjs(b).valueOf();
        } else if (period === "quarterly") {
          return a.localeCompare(b);
        } else {
          return a.localeCompare(b);
        }
      });

      // Format categories for display
      const categories = sortedDates.map((date) => {
        if (period === "weekly") {
          return dayjs(date).format("MMM DD");
        } else if (period === "monthly") {
          return dayjs(date).format("YYYY-MM");
        } else if (period === "quarterly") {
          return date;
        } else {
          return date;
        }
      });

      // Create series for each location/city
      const seriesNames = Object.keys(groupedData).sort();
      const series = seriesNames.map((name) => ({
        name,
        data: sortedDates.map((date) => groupedData[name][date] || 0),
      }));

      return { categories, series, seriesNames };
    }, [data, filterType, period]);

    // Map colors based on series names
    const colors = useMemo(() => {
      if (filterType === "city") {
        return seriesNames.map((name) => getCityColor(name));
      }
      return seriesNames.map((name) => getStoreColor(name));
    }, [seriesNames, filterType]);

    const options: ApexOptions = {
      chart: {
        type: "bar",
        height: 350,
        zoom: { enabled: false },
        toolbar: { show: false },
        stacked: false,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
          borderRadius: 0,
          dataLabels: {
            position: "top",
          },
        },
      },
      dataLabels: { enabled: false },
      grid: {
        strokeDashArray: 5,
        padding: {
          right: 30,
          left: 10,
        },
      },
      xaxis: {
        categories,
        labels: {
          rotate: -45,
          trim: false,
          style: {
            cssClass: "apexcharts-xaxis-label",
          },
        },
      },
      yaxis: {
        title: {
          text: "Number of Droppers",
        },
        labels: {
          formatter: (value: number) => {
            const safe =
              typeof value === "number" && !Number.isNaN(value) ? value : 0;
            return safe.toLocaleString();
          },
        },
      },
      colors: colors.length > 0 ? colors : ["#808080"],
      legend: {
        position: "right",
        horizontalAlign: "left",
        markers: { size: 2 },
      },
      fill: {
        opacity: 1,
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
          const seriesData = series
            .map((seriesValues: any, index: number) => ({
              name: w.globals.seriesNames[index],
              value: seriesValues[dataPointIndex],
              color: w.config.colors[index],
            }))
            .sort((a: any, b: any) => b.value - a.value);

          let html =
            '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">' +
            (categories?.[dataPointIndex] ?? "") +
            "</div>";

          seriesData.forEach((item: any) => {
            html += `
            <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
              <span class="apexcharts-tooltip-marker" style="background-color: ${item.color};"></span>
              <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                <div class="apexcharts-tooltip-y-group">
                  <span class="apexcharts-tooltip-text-y-label">${item.name}: </span>
                  <span class="apexcharts-tooltip-text-y-value">${item.value.toLocaleString()}</span>
                </div>
              </div>
            </div>`;
          });

          return html;
        },
      },
    };

    if (isLoading) {
      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-gray-500">No data available</div>
        </div>
      );
    }

    return (
      <div className="w-full">
        <Chart options={options} series={series} type="bar" height={350} />
      </div>
    );
  }
);

PlasticVolumesPerDroppers.displayName = "PlasticVolumesPerDroppers";
